/**
 * useEngineBatchRuns (P144) -- generic poll + trigger for the shared
 * engine_batch_runs substrate. Parameterised by the list path (per module)
 * + a per-kind trigger path, so both the obligation console (framework) and
 * the derive console (twin) reuse it.
 *
 * Admin-only (callers gate the surrounding UI with useMe). The list query
 * polls at 2s while any run is active, then stops (the P35 cadence).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import { api } from "shared/api/client";
import type { EngineBatchRun } from "shared/api/types";

export function isActive(run: EngineBatchRun): boolean {
  return run.status === "queued" || run.status === "running";
}

export function useEngineBatchRuns(
  listPath: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<EngineBatchRun[]> {
  return useQuery<EngineBatchRun[]>({
    queryKey: ["engine-batch-runs", listPath],
    queryFn: () => api.get<EngineBatchRun[]>(listPath),
    enabled: options.enabled ?? true,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && data.some(isActive) ? 2000 : false;
    },
  });
}

export function useTriggerEngineBatchRun(opts: {
  listPath: string;
  triggerPath: (kind: string) => string;
}): UseMutationResult<EngineBatchRun, Error, string> {
  const qc = useQueryClient();
  return useMutation<EngineBatchRun, Error, string>({
    mutationFn: (kind) => api.post<EngineBatchRun>(opts.triggerPath(kind), {}),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["engine-batch-runs", opts.listPath],
      });
    },
  });
}

/** P147.3: request cancellation of any run (cross-module shared endpoint). */
export function useCancelEngineBatchRun(opts: {
  listPath: string;
}): UseMutationResult<EngineBatchRun, Error, string> {
  const qc = useQueryClient();
  return useMutation<EngineBatchRun, Error, string>({
    mutationFn: (runId) =>
      api.post<EngineBatchRun>(`/v1/admin/engine-runs/${runId}/cancel`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["engine-batch-runs", opts.listPath],
      });
    },
  });
}
